import { motion, AnimatePresence } from "framer-motion";
import { CreditCard } from "lucide-react";

export const CheckoutModal = ({
  showPaymentModal,
  setShowPaymentModal,
  shippingMethod,
  setShippingMethod,
  quantity,
  PRODUCT_PRICE,
  shippingCost,
  totalPrice,
  customerName,
  setCustomerName,
  customerPhone,
  setCustomerPhone,
  handleCheckout,
  isLoading,
  t
}) => (
  <AnimatePresence>
    {showPaymentModal && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4"
        onClick={() => setShowPaymentModal(false)}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_mex-catnip-promo/artifacts/eeist4qj_LOGO%20FONDO%20TRANSPARENTE.png" 
              alt="GatoFeliz" 
              className="h-12 mx-auto mb-4"
            />
            <h3 className="font-gluten text-2xl text-agave mb-2">
              {t("payment_modal_title") || "Selecciona tu metodo de pago"}
            </h3>
          </div>

          {/* Shipping Selection */}
          <div className="mb-4">
            <p className="font-nunito text-sm text-muted-foreground mb-2">Como quieres recibir tu pedido?</p>
            <div className="space-y-2">
              <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${shippingMethod === "pickup" ? "border-agave bg-agave/5" : "border-gray-200 hover:border-gray-300"}`}>
                <input type="radio" name="shipping" value="pickup" checked={shippingMethod === "pickup"} onChange={() => setShippingMethod("pickup")} className="w-4 h-4 text-agave" />
                <div className="flex-1">
                  <p className="font-fredoka font-bold text-sm">Recoleccion en Queretaro</p>
                  <p className="font-nunito text-xs text-muted-foreground">Coordinaremos la entrega en la ciudad</p>
                </div>
                <span className="font-fredoka font-bold text-agave">Gratis</span>
              </label>
              <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${shippingMethod === "delivery" ? "border-agave bg-agave/5" : "border-gray-200 hover:border-gray-300"}`}>
                <input type="radio" name="shipping" value="delivery" checked={shippingMethod === "delivery"} onChange={() => setShippingMethod("delivery")} className="w-4 h-4 text-agave" />
                <div className="flex-1">
                  <p className="font-fredoka font-bold text-sm">Envio a domicilio</p>
                  <p className="font-nunito text-xs text-muted-foreground">A cualquier parte de Mexico</p>
                </div>
                <span className="font-fredoka font-bold text-mexican">+$150/caja</span>
              </label>
            </div>
          </div>

          {/* Price Summary */}
          <div className="mb-4 p-3 bg-bone rounded-xl">
            <div className="flex justify-between font-nunito text-sm">
              <span>Producto ({quantity}x)</span>
              <span>${(PRODUCT_PRICE * quantity).toLocaleString()} MXN</span>
            </div>
            <div className="flex justify-between font-nunito text-sm">
              <span>Envio {shippingMethod === "delivery" ? `(${quantity}x $150)` : ""}</span>
              <span>{shippingMethod === "delivery" ? `$${shippingCost.toLocaleString()} MXN` : "Gratis"}</span>
            </div>
            <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between font-fredoka font-bold">
              <span>Total</span>
              <span className="text-mexican">${totalPrice.toLocaleString()} MXN</span>
            </div>
          </div>

          {/* Customer Name Input (Optional) */}
          <div className="mb-4">
            <p className="font-nunito text-sm text-muted-foreground mb-2">Tu nombre (opcional)</p>
            <input type="text" placeholder="Ej: Juan Perez" value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-agave focus:outline-none font-nunito" data-testid="checkout-customer-name" />
          </div>

          {/* Phone Input */}
          <div className="mb-4">
            <p className="font-nunito text-sm text-muted-foreground mb-2">Tu numero de telefono</p>
            <input type="tel" placeholder="Ej: 442 123 4567" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-agave focus:outline-none font-nunito" />
          </div>

          <div className="space-y-3 mb-6">
            {/* Stripe */}
            <button onClick={() => handleCheckout("stripe")} disabled={isLoading || !customerPhone.trim()} className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-200 hover:border-agave hover:bg-agave/5 transition-all group disabled:opacity-50 disabled:cursor-not-allowed" data-testid="payment-stripe-btn">
              <div className="w-12 h-12 bg-[#635BFF] rounded-xl flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
              <div className="text-left flex-1">
                <p className="font-fredoka font-bold text-foreground group-hover:text-agave">{t("payment_card") || "Tarjeta de Credito/Debito"}</p>
                <p className="font-nunito text-sm text-muted-foreground">Visa, Mastercard, AMEX</p>
              </div>
            </button>

            {/* MercadoPago */}
            <button onClick={() => handleCheckout("mercadopago")} disabled={isLoading || !customerPhone.trim()} className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-200 hover:border-[#00AEEF] hover:bg-[#00AEEF]/5 transition-all group disabled:opacity-50 disabled:cursor-not-allowed" data-testid="payment-mercadopago-btn">
              <div className="w-12 h-12 bg-[#00AEEF] rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="white"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9v-2h2v2zm0-4H9V7h2v5zm4 4h-2v-2h2v2zm0-4h-2V7h2v5z"/></svg>
              </div>
              <div className="text-left flex-1">
                <p className="font-fredoka font-bold text-foreground group-hover:text-[#00AEEF]">Mercado Pago</p>
                <p className="font-nunito text-sm text-muted-foreground">Tarjetas, transferencia, efectivo</p>
              </div>
            </button>

            {/* PayPal */}
            <button onClick={() => handleCheckout("paypal")} disabled={isLoading || !customerPhone.trim()} className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-200 hover:border-[#003087] hover:bg-[#003087]/5 transition-all group disabled:opacity-50 disabled:cursor-not-allowed" data-testid="payment-paypal-btn">
              <div className="w-12 h-12 bg-[#003087] rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="white"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/></svg>
              </div>
              <div className="text-left flex-1">
                <p className="font-fredoka font-bold text-foreground group-hover:text-[#003087]">PayPal</p>
                <p className="font-nunito text-sm text-muted-foreground">Pago seguro con PayPal</p>
              </div>
            </button>
          </div>

          <button onClick={() => setShowPaymentModal(false)} className="w-full py-3 text-muted-foreground font-fredoka hover:text-foreground transition-colors">
            {t("cancel") || "Cancelar"}
          </button>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);
